import { ArrowRight, ShieldCheck, UserCheck } from 'lucide-react';
import { UI_STRINGS } from '@/lib/constants/ui-strings';
import { Button } from '@/components/ui/Button';
import { AuthHero } from '@/components/common/AuthHero';
import { SectionCard } from '@/components/common/SectionCard';
import { useAcceptInviteAuthenticated } from '@/hooks/useAcceptInviteAuthenticated';

interface InviteAcceptConfirmCardProps {
  token: string;
  entityName: string;
}

/**
 * The `acceptMethod: 'login'` accept screen (GitHub-style): the invitee
 * already has a verified account and is logged in as the invited email, so
 * this just confirms and joins them - no password form, no OTP.
 */
export function InviteAcceptConfirmCard({ token, entityName }: InviteAcceptConfirmCardProps) {
  const I = UI_STRINGS.INVITE_ACCEPT_CONFIRM;
  const acceptInvite = useAcceptInviteAuthenticated(token);

  return (
    <div className="flex flex-col items-center bg-page-bg px-4 py-16">
      <AuthHero
        badgeIcon={<ShieldCheck size={14} />}
        badgeText={I.BADGE}
        heading={entityName}
      />

      <div className="mt-10 w-full max-w-lg">
        <SectionCard className="flex flex-col items-center gap-6 text-center">
          <span
            aria-hidden="true"
            className="flex h-14 w-14 items-center justify-center rounded-full bg-green/10 text-dkgreen"
          >
            <UserCheck size={28} />
          </span>
          <p className="text-sm text-slate">{I.BODY}</p>

          {acceptInvite.isError && (
            <p role="alert" className="text-sm text-danger">
              {I.ERROR}
            </p>
          )}

          <Button
            type="button"
            variant="navy"
            disabled={acceptInvite.isPending}
            onClick={() => acceptInvite.mutate()}
            className="inline-flex w-full items-center justify-center gap-2 py-3.5 text-base drop-shadow-md"
          >
            {acceptInvite.isPending ? I.ACCEPTING : I.CTA}
            <ArrowRight size={18} aria-hidden="true" />
          </Button>
        </SectionCard>
      </div>
    </div>
  );
}
